import logError from '@/utils/logError/logError';
import offeringService from '@/modules/purchasable/services/offeringService';
import quotaBalanceService from '@/modules/usage/services/quotaBalanceService';
import { groupBySku } from '@/modules/usage/services/adapters/quotaBalancesAdapter';
import { toUsageRows as smsRows, fromBalance as smsBalanceRows } from '@/modules/usage/services/adapters/smsAdapter';
import {
  SKUS, COUNTABLE_SKUS, SMS_FEATURE, iconFor, isSingleCharge, isPurchasable,
} from '@/modules/usage/constants/countables';

const state = {
  balances: [],
  totals: [],
  balancesLoaded: false,
  isLoadingBalances: false,
  balancesError: false,
  offerings: [],
  offeringsLoaded: false,
};

const actions = {
  async loadQuotaBalances({ commit, state: currentState }, { force = false } = {}) {
    if (currentState.balancesLoaded && !force) return;

    commit('setLoadingBalances', true);
    commit('setBalancesError', false);

    try {
      commit('setBalances', await quotaBalanceService.fetchQuotaBalances());
    } catch (error) {
      logError('Failed to load quota balances', error);
      commit('setBalances', { balances: [], totals: [] });
      commit('setBalancesError', true);
    } finally {
      commit('setLoadingBalances', false);
    }
  },

  async loadOfferings({ commit, state: currentState }, { force = false } = {}) {
    if (currentState.offeringsLoaded && !force) return;

    try {
      commit('setOfferings', await offeringService.fetchPurchasableOfferings({
        isActive: true,
        sort: 'created_at:asc',
      }));
    } catch (error) {
      logError('Failed to load offerings', error);
      commit('setOfferings', []);
    }
  },

  async loadSmsUsage({ dispatch, rootGetters }) {
    if (!rootGetters['BusinessStore/hasFeature'](SMS_FEATURE)) return;

    const businessId = rootGetters['AuthStore/businessUid'];

    try {
      await Promise.all([
        dispatch('BusinessStore/businessSms/loadSmsAvailability', undefined, { root: true }),
        dispatch('BusinessStore/businessSms/loadSmsQuotas', { businessId }, { root: true }),
      ]);
      if (rootGetters['BusinessStore/businessSms/smsQuotas']) return;

      await dispatch('BusinessStore/businessSms/loadSmsBalance', businessId, { root: true });
    } catch (error) {
      logError('Failed to load the SMS usage', error);
    }
  },

  async loadUsage({ dispatch }) {
    await Promise.all([
      dispatch('loadQuotaBalances'),
      dispatch('loadOfferings'),
      dispatch('loadSmsUsage'),
    ]);
  },
};

const mutations = {
  setBalances(state, { balances, totals }) {
    state.balances = balances;
    state.totals = totals;
    state.balancesLoaded = true;
  },
  setLoadingBalances(state, isLoading) {
    state.isLoadingBalances = isLoading;
  },
  setBalancesError(state, hasError) {
    state.balancesError = hasError;
  },
  setOfferings(state, offerings) {
    state.offerings = offerings;
    state.offeringsLoaded = true;
  },
};

const sumOf = (rows, field) => rows.reduce((sum, row) => sum + row[field], 0);

const newerThan = (purchase, latest) => !latest
  || new Date(purchase.created_at) > new Date(latest.created_at);

const latestPurchase = (purchases) => purchases
  .reduce((latest, purchase) => (newerThan(purchase, latest) ? purchase : latest), null);

const repeatablePurchase = (purchases) => {
  const latest = latestPurchase(purchases);
  return isSingleCharge(latest) ? latest : null;
};

const buildCountable = (sku, rows, offerings, purchases = [], total = null) => {
  const unlimited = total ? total.left === null : rows.some((row) => row.unlimited);
  const lastPurchase = repeatablePurchase(purchases);
  const purchasableOfferings = offerings.filter(isPurchasable);
  const measured = rows.filter((row) => !row.unlimited
    && typeof row.credit === 'number'
    && typeof row.left === 'number');
  const hasTotals = measured.length > 0;
  const summedLeft = hasTotals ? sumOf(measured, 'left') : null;
  const totalLeft = total ? total.left : summedLeft;

  return {
    sku,
    icon: iconFor(sku),
    rows,
    unlimited,
    totalLeft,
    totalCredit: hasTotals ? sumOf(measured, 'credit') : null,
    totalConsumed: hasTotals ? sumOf(measured, 'consumed') : null,
    isEmpty: rows.length === 0,
    isExhausted: !unlimited && totalLeft !== null && totalLeft <= 0,
    offerings,
    purchasableOfferings,
    canPurchase: purchasableOfferings.length > 0,
    lastPurchase,
    autoReloadOffering: lastPurchase
      ? offerings.find((offering) => offering.uid === lastPurchase.offering_uid) || null
      : null,
  };
};

const groupPurchasesBySku = (extras) => (extras || []).reduce((acc, extra) => {
  const sku = extra?.sku;
  if (!sku) return acc;
  acc[sku] = acc[sku] || [];
  acc[sku].push(extra);
  return acc;
}, {});

const getters = {
  isLoadingUsage: (state) => state.isLoadingBalances,
  hasUsageError: (state) => state.balancesError,

  offeringsBySku: (state) => state.offerings.reduce((acc, offering) => {
    const sku = offering?.SKU;
    if (!sku) return acc;
    acc[sku] = acc[sku] || [];
    acc[sku].push(offering);
    return acc;
  }, {}),

  countables: (state, localGetters, rootState, rootGetters) => {
    const rowsBySku = groupBySku(state.balances);
    const offeringsBySku = localGetters.offeringsBySku;
    const purchasesBySku = groupPurchasesBySku(rootGetters['SubscriptionStore/getExtras']);
    const totalFor = (sku) => (state.totals || []).find((entry) => entry?.sku === sku) || null;
    const smsQuotas = rootGetters['BusinessStore/businessSms/smsQuotas'];

    const smsBalance = rootGetters['BusinessStore/businessSms/smsBalance'];
    const smsOwnRows = () => (smsQuotas ? smsRows(smsQuotas) : smsBalanceRows(smsBalance));
    const rowsFor = (sku) => (sku === SKUS.SMS ? smsOwnRows() : rowsBySku[sku] || []);
    const smsEnabled = !!rootGetters['BusinessStore/hasFeature'](SMS_FEATURE);
    const hasSms = smsEnabled && !!(offeringsBySku[SKUS.SMS] || purchasesBySku[SKUS.SMS]);

    return COUNTABLE_SKUS
      .filter((sku) => sku !== SKUS.SMS || hasSms)
      .map((sku) => buildCountable(
        sku,
        rowsFor(sku),
        offeringsBySku[sku] || [],
        purchasesBySku[sku] || [],
        totalFor(sku),
      ))
      .filter((countable) => !countable.isEmpty || countable.canPurchase);
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
